import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Sparkles, Award, Beaker, Users, Compass, Quote, MapPin, Heart } from "lucide-react";
import { api } from "@/lib/apiClient";
import { BRAND } from "@/constants/brand";
import { CATEGORY_WORLDS, CATEGORY_ORDER, PRIVATE_NOTICE } from "@/constants/ingredients";
import PopsicleSVG from "@/components/PopsicleSVG";
import useSections from "@/hooks/useSections";
import PandaClubShowcase from "@/components/PandaClubShowcase";
import LoyaltyJourneyShowcase from "@/components/LoyaltyJourneyShowcase";
import CommunityVaultShowcase from "@/components/CommunityVaultShowcase";

const VALUES = [
  { icon: Beaker, title: "Innovation Driven", text: "A working lab behind every flavour. We test, fail, retry — and document it." },
  { icon: Award, title: "Quality Focused", text: "Single-origin ingredients, slow-set freezing, honest labels." },
  { icon: Users, title: "Customer Centric", text: "Designed in conversation with our customers, students and partners." },
  { icon: Sparkles, title: "Growing Community", text: "Across Karnataka and beyond — one Pop at a time." },
  { icon: Compass, title: "Future Ready", text: "A modern, transparent and responsibly-grown popsicle brand." },
];

const STORY = [
  { step: "01", title: "Curiosity", body: "An observation: frozen desserts looked good but rarely felt fresh or exciting." },
  { step: "02", title: "Experimentation", body: "Hundreds of recipes, freezer tests, and tasting notes from a tiny kitchen." },
  { step: "03", title: "Community", body: "Customers became inventors. Inventors became the menu." },
  { step: "04", title: "Pop Panda Today", body: "A premium Indian popsicle brand expanding slowly — and deliberately." },
];

export default function Home() {
  const [reviews, setReviews] = useState([]);
  const [hof, setHof] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [labFlavours, setLabFlavours] = useState([]);
  const [challenges, setChallenges] = useState([]);
  const [contributors, setContributors] = useState([]);
  const { isVisible } = useSections();
  useEffect(() => {
    api.get("/reviews").then((r) => setReviews(r.data || []));
    api.get("/dream-pops/public").then((r) => setHof((r.data || []).slice(0, 3)));
    api.get("/milestones").then((r) => setMilestones((r.data || []).slice(0, 4)));
    api.get("/lab-flavours").then((r) => setLabFlavours((r.data || []).slice(0, 3)));
    api.get("/challenges").then((r) => setChallenges((r.data || []).slice(0, 3)));
    api.get("/contributors").then((r) => setContributors((r.data || []).slice(0, 8)));
  }, []);

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="section-pad pt-16 lg:pt-24">
        <div className="container-pp grid gap-10 lg:grid-cols-12 items-center">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <span className="tag" data-testid="hero-tag"><Sparkles size={14} /> Karnataka • Est. 2024</span>
              <span className="text-xs uppercase tracking-[0.22em] text-brand-charcoal/55">{BRAND.tagline}</span>
            </div>
            <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9 }}
              data-testid="hero-headline" className="editorial-h1 text-5xl sm:text-6xl md:text-7xl lg:text-8xl text-brand-charcoal">
              Crafted Happiness,
              <br /><em className="font-display italic">one Pop</em> at a time.
            </motion.h1>
            <p className="mt-8 max-w-xl text-brand-charcoal/70 text-lg leading-relaxed">
              Pop Panda is a premium Indian popsicle brand built on innovation, curiosity and community.
              We don't show a menu — we open a door. Step into our worlds.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/worlds" className="btn-pill btn-dark" data-testid="hero-cta-worlds">Enter Pop Panda Worlds <ArrowUpRight size={16} className="arrow" /></Link>
              <Link to="/flavour-creator" className="btn-pill btn-primary" data-testid="hero-cta-create">Create Your Own Popsicle</Link>
              <Link to="/request-stock" className="btn-pill btn-outline-dark" data-testid="hero-cta-request">Request Stock</Link>
            </div>
            <div className="mt-12 grid grid-cols-3 gap-6 max-w-md">
              {[{ n: "90+", l: "Ingredients in Studio" }, { n: "5", l: "Worlds" }, { n: "1", l: "Stick at a Time" }].map((s) => (
                <div key={s.l}>
                  <div className="font-display text-4xl text-brand-charcoal">{s.n}</div>
                  <div className="text-xs uppercase tracking-[0.2em] text-brand-charcoal/55 mt-1">{s.l}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="relative aspect-square max-w-md mx-auto">
              <div className="absolute inset-0 rounded-full bg-brand-yellow/30 blur-3xl" />
              <div className="absolute inset-6 rounded-full border border-dashed border-brand-charcoal/30 animate-spin-slow" />
              <div className="relative h-full flex items-center justify-center">
                <PopsicleSVG primary="#FF4F5A" secondary="#FFC93C" className="h-[70%] drop-shadow-2xl" />
              </div>
              <div className="absolute -left-2 top-10 card-soft px-3 py-2 text-xs animate-float-y">🥭 Summer</div>
              <div className="absolute right-0 top-1/3 card-soft px-3 py-2 text-xs animate-float-y" style={{ animationDelay: "1.2s" }}>🍫 Comfort</div>
              <div className="absolute -bottom-2 left-1/3 card-soft px-3 py-2 text-xs animate-float-y" style={{ animationDelay: "2.4s" }}>🧪 Innovation</div>
            </div>
          </div>
        </div>
      </section>

      {/* Marquee */}
      
      <section className="py-6 bg-brand-charcoal text-brand-cream overflow-hidden">
        <div className="marquee-track animate-marquee">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="flex items-center gap-12 px-6 shrink-0">
              {["Premium Popsicles", "Innovation Lab", "Karnataka Crafted", "Community Inventors", "Slow-Set Frozen", "Pop Panda Go", "Happiness on a Stick"].map((t, j) => (
                <span key={j} className="font-display text-3xl whitespace-nowrap">★ {t}</span>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* Story */}
      <section className="section-pad" data-testid="home-story">
        <div className="container-pp grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <div className="editorial-divider mb-5" />
            <div className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Our Story</div>
            <h2 className="font-display text-5xl mt-3 leading-none">A small idea, frozen slowly into a movement.</h2>
            <p className="mt-6 text-brand-charcoal/70 leading-relaxed">Pop Panda was born from curiosity — and a stubborn belief that frozen desserts should feel as fresh as the moment they were imagined.</p>
            <Link to="/about" className="btn-pill btn-outline-dark mt-8 text-sm" data-testid="home-story-cta">Read full story <ArrowUpRight size={16} className="arrow" /></Link>
          </div>
          <ol className="lg:col-span-8 grid sm:grid-cols-2 gap-6">
            {STORY.map((s) => (
              <li key={s.step} className="card-elevated p-7">
                <div className="text-[11px] uppercase tracking-[0.22em] text-brand-red">{s.step}</div>
                <div className="font-display text-3xl mt-2">{s.title}</div>
                <p className="mt-3 text-sm text-brand-charcoal/70 leading-relaxed">{s.body}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Worlds — removed from homepage per request; available at /worlds and in nav */}

      {/* Panda Club — flagship membership showcase */}
      <PandaClubShowcase />

      {/* Popsicle Loyalty Journey — flagship */}
      <LoyaltyJourneyShowcase />

      {/* Community Vault — cinematic community goal */}
      <CommunityVaultShowcase />

      {/* Inventor CTA */}
      <section className="section-pad" data-testid="home-inventor">
        <div className="container-pp grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6">
            <div className="text-[11px] uppercase tracking-[0.22em] text-brand-red">Become a Pop Panda Inventor</div>
            <h2 className="font-display text-5xl md:text-6xl mt-3 leading-none">Your Idea Could Become Our Next Bestseller.</h2>
            <p className="mt-6 text-brand-charcoal/70 max-w-lg leading-relaxed">Millions enjoy popsicles. Few get to invent them. Walk through our Flavour Creator Studio — pick from 90+ ingredients, mix the ratios, and submit your creation to the lab.</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/flavour-creator" className="btn-pill btn-dark" data-testid="home-inventor-cta">Create Your Own Popsicle <ArrowUpRight size={16} className="arrow" /></Link>
              <Link to="/hall-of-fame" className="btn-pill btn-outline-dark" data-testid="home-hof-cta">See Hall of Fame</Link>
            </div>
          </div>
          <div className="lg:col-span-6 grid grid-cols-3 gap-3">
            {hof.length > 0 ? hof.map((h, i) => (
              <div key={h.id} className="card-elevated p-4 aspect-[3/4] flex flex-col" style={{ transform: `rotate(${(i - 1) * 2}deg)` }}>
                <div className="flex-1 grid place-items-center bg-brand-muted rounded-2xl">
                  <PopsicleSVG primary={h.color_primary} secondary={h.color_secondary} className="h-2/3" />
                </div>
                <div className="font-display text-base mt-3 truncate">{h.pop_name}</div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-brand-red truncate">{h.customer_name}</div>
              </div>
            )) : ["#FF4F5A","#FFC93C","#7DD3C0"].map((c, i) => (
              <div key={c} className="card-elevated p-4 aspect-[3/4] flex flex-col" style={{ transform: `rotate(${(i-1)*2}deg)` }}>
                <div className="flex-1 grid place-items-center bg-brand-muted rounded-2xl"><PopsicleSVG primary={c} secondary="#FFC93C" className="h-2/3" /></div>
                <div className="font-display text-base mt-3">Your idea here</div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-brand-red">Submit yours</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why */}
      <section className="section-pad bg-brand-charcoal text-brand-cream" data-testid="home-why">
        <div className="container-pp grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="text-[11px] uppercase tracking-[0.22em] text-brand-yellow">Why Pop Panda</div>
            <h2 className="font-display text-5xl mt-3 leading-none text-brand-cream">A brand built on five quiet promises.</h2>
            <p className="mt-6 text-brand-cream/70 max-w-md">Each value here is a choice we make in the kitchen every single day.</p>
          </div>
          <div className="lg:col-span-8 grid sm:grid-cols-2 gap-5">
            {VALUES.map((v) => (
              <div key={v.title} className="rounded-3xl border border-white/10 bg-white/5 p-7">
                <v.icon size={22} className="text-brand-yellow" />
                <div className="font-display text-2xl mt-4">{v.title}</div>
                <p className="mt-2 text-sm text-brand-cream/70 leading-relaxed">{v.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Request Stock band */}
      <section className="section-pad" data-testid="home-stock">
        <div className="container-pp card-elevated p-10 lg:p-16 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="text-[11px] uppercase tracking-[0.22em] text-brand-red flex items-center gap-2"><MapPin size={14} /> For our community</div>
            <h2 className="font-display text-4xl md:text-5xl mt-3">Can't find Pop Panda nearby?</h2>
            <p className="mt-4 text-brand-charcoal/70 max-w-xl">Tell us your shop, school or college canteen — and we'll bring Pop Panda to your area. High-demand areas move to the top of our expansion list.</p>
          </div>
          <div className="lg:col-span-5 lg:text-right">
            <Link to="/request-stock" className="btn-pill btn-dark" data-testid="home-stock-cta">Request Stock Near Me <ArrowUpRight size={16} className="arrow" /></Link>
          </div>
        </div>
      </section>

      {/* Students */}
      <section className="section-pad" data-testid="home-students">
        <div className="container-pp">
          <div className="relative max-w-5xl mx-auto rounded-[2.5rem] bg-brand-cream border border-brand-line/60 overflow-hidden">
            {/* Decorative kitchen lines — pure CSS, no images */}
            <div aria-hidden="true" className="absolute inset-0 pointer-events-none opacity-[0.06]"
              style={{ backgroundImage: "repeating-linear-gradient(135deg, #2B2B2B 0 1px, transparent 1px 24px)" }} />
            <div aria-hidden="true" className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-brand-yellow/25 blur-3xl" />
            <div aria-hidden="true" className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full bg-brand-red/15 blur-3xl" />

            <div className="relative px-8 py-16 lg:px-20 lg:py-24 grid lg:grid-cols-12 gap-10 items-end">
              <div className="lg:col-span-7">
                <div className="text-[11px] uppercase tracking-[0.3em] text-brand-red flex items-center gap-2">
                  <span className="inline-block w-6 h-px bg-brand-red" /> For Students
                </div>
                <h2 className="editorial-h1 text-5xl md:text-6xl lg:text-7xl mt-5 leading-[0.95]">
                  Help us <em className="font-display italic">build</em><br />Pop Panda.
                </h2>
                <p className="mt-8 text-brand-charcoal/70 leading-relaxed max-w-xl">
                  From ideas and content to food production and operations — we welcome students who want to contribute, collaborate or help grow the brand. No formal application. Skills and enthusiasm matter more than qualifications.
                </p>
                <div className="mt-10 flex flex-wrap gap-3">
                  <Link to="/student-opportunities" className="btn-pill btn-dark text-sm" data-testid="home-students-cta">
                    Join the team <ArrowUpRight size={16} className="arrow" />
                  </Link>
                  <Link to="/contributors" className="btn-pill btn-ghost border border-brand-line text-sm">
                    See contributors wall
                  </Link>
                </div>
              </div>

              {/* Typographic counter — no image, just numerals as design */}
              <div className="lg:col-span-5 lg:border-l lg:border-brand-charcoal/15 lg:pl-12">
                <div className="grid grid-cols-2 gap-y-8 gap-x-6">
                  {[
                    { n: "Anyone", l: "can apply" },
                    { n: "No CV", l: "needed" },
                    { n: "Real", l: "projects" },
                    { n: "By name", l: "credited" },
                  ].map((s) => (
                    <div key={s.l}>
                      <div className="font-display text-3xl md:text-4xl leading-none">{s.n}</div>
                      <div className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55 mt-2">{s.l}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Reviews */}
      <section className="section-pad bg-white" data-testid="home-reviews">
        <div className="container-pp">
          <div className="flex items-end justify-between mb-12 flex-col lg:flex-row gap-6">
            <div>
              <div className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Voices</div>
              <h2 className="font-display text-5xl mt-3 leading-none max-w-xl">Loved by our most honest critics.</h2>
            </div>
            <Link to="/feedback" className="btn-pill btn-outline-dark text-sm" data-testid="home-reviews-cta">Leave Feedback</Link>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {reviews.slice(0, 3).map((r) => (
              <div key={r.id} className="card-elevated p-7 grain-overlay">
                <Quote size={20} className="text-brand-red" />
                <div className="mt-3 text-lg font-display leading-snug">{r.title || r.body.slice(0, 60) + "…"}</div>
                <p className="mt-4 text-sm text-brand-charcoal/70 leading-relaxed">{r.body}</p>
                <div className="mt-6 flex items-center justify-between">
                  <div className="text-sm font-medium">{r.customer_name}</div>
                  <div className="text-xs text-brand-charcoal/55">{"★".repeat(r.rating || 5)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="section-pad" data-testid="home-contact">
        <div className="container-pp grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Visit / Order</div>
            <h2 className="font-display text-5xl mt-3 leading-none">Find us across Karnataka. Or a quick WhatsApp away.</h2>
            <p className="mt-6 text-brand-charcoal/70 max-w-lg">Visit your nearest outlet or contact us to discover today's available flavours. Each batch is shaped by the season.</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href={BRAND.whatsappUrl} target="_blank" rel="noreferrer" className="btn-pill btn-primary text-sm" data-testid="home-contact-wa">WhatsApp Us</a>
              <Link to="/event-orders" className="btn-pill btn-outline-dark text-sm" data-testid="home-contact-event">Book an Event</Link>
            </div>
          </div>
          <div className="lg:col-span-5 grid gap-4">
            <div className="card-elevated p-6">
              <div className="text-[10px] uppercase tracking-[0.22em] text-brand-red">Manufacturing</div>
              <div className="font-display text-xl mt-2">{BRAND.addresses.manufacturing.line1}</div>
              <div className="text-sm text-brand-charcoal/70 mt-2">{BRAND.addresses.manufacturing.lines.join(", ")}</div>
            </div>
            <div className="card-elevated p-6">
              <div className="text-[10px] uppercase tracking-[0.22em] text-brand-red">Retail (Pop Panda Go)</div>
              <div className="font-display text-xl mt-2">{BRAND.addresses.retail.line1}</div>
              <div className="text-sm text-brand-charcoal/70 mt-2">{BRAND.addresses.retail.lines.join(", ")}</div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
